import { CSSProperties, ReactElement, ReactNode } from 'react'

import { Button } from 'antd'
import noop from 'lodash/noop'
import { useTranslation } from 'react-i18next'

import { IconButton } from 'components/Buttons'
import { Icon } from 'components/core'
import { MinimalTitle } from 'components/MinimalTitle/MinimalTitle'
import TableSettingsModal from 'components/PaginatedTable/TableSettingsModal/TableSettingsModal'
import { useTableSettingsProps } from 'hooks/redux'
import { CobaltPath } from 'routing/Path'
import { Field, PeriodDeltaType } from 'types'

type DownloadActionButtonVariant = 'icon-button' | 'button'

interface Props<RecordType> {
    header?: ReactNode
    title: ReactNode
    subtitle?: ReactNode
    footer?: ReactNode
    tablePath: CobaltPath
    downloadData?: () => void
    downloadActionVariant?: DownloadActionButtonVariant
    downloading?: boolean
    columns: Field<RecordType>[]
    showPeriodDeltas?: boolean
    periodDeltaType?: PeriodDeltaType
    periodDeltaDateRange?: string[]
    disablePriorPeriod?: boolean
    allowCustomPriorPeriod?: boolean
    disabledPriorPeriodMessage?: string
    showTotalRow?: boolean
    showTableSettings?: boolean
    extraActions?: ReactNode[]
    extraActionsStyles?: CSSProperties
    dataSourceKey?: string
}

const BasicTitle = <RecordType,>({
    header,
    title,
    subtitle,
    footer,
    tablePath,
    downloadData = noop,
    downloadActionVariant = 'icon-button',
    downloading = false,
    showPeriodDeltas,
    periodDeltaType,
    periodDeltaDateRange,
    disablePriorPeriod,
    allowCustomPriorPeriod = true,
    disabledPriorPeriodMessage,
    showTotalRow,
    showTableSettings = true,
    columns,
    extraActions = [],
    extraActionsStyles,
    dataSourceKey,
}: Props<RecordType>): ReactElement => {
    const { t } = useTranslation('table')
    const {
        toggleModalVisibility,
        isSettingsModalVisible,
        handleModalCancel,
        handleModalOk,
    } = useTableSettingsProps(tablePath)

    const getExtraActions = (): ReactNode[] => {
        let actions = extraActions

        if (downloadData !== noop) {
            const downloadAction =
                downloadActionVariant === 'icon-button' ? (
                    <IconButton
                        tooltipTitle={t(
                            'table:BasicTitle.downloadBtn.tooltipTitle',
                            'Download Report'
                        )}
                        tooltipPlacement="topRight"
                        onClick={() => downloadData()}
                        icon={<Icon name="DOWNLOAD" />}
                        type="link"
                        loading={downloading}
                    />
                ) : (
                    <Button
                        onClick={() => downloadData()}
                        icon={<Icon name="DOWNLOAD" />}
                        variant="outlined"
                        size="middle"
                        loading={downloading}
                        style={{ marginRight: 4 }}
                    >
                        Export
                    </Button>
                )
            actions = actions.concat(downloadAction)
        }

        if (showTableSettings) {
            actions = actions.concat(
                <IconButton
                    tooltipTitle={t(
                        'table:BasicTitle.tableSettingsBtn.tooltipTitle',
                        'Adjust Table Settings'
                    )}
                    tooltipPlacement="topRight"
                    onClick={toggleModalVisibility}
                    icon={<Icon name="SETTINGS" size={15} />}
                    type="link"
                />
            )
        }
        return actions
    }

    return (
        <>
            <MinimalTitle
                header={header}
                title={title}
                subtitle={subtitle}
                footer={footer}
                extraActions={getExtraActions()}
                extraActionsStyles={extraActionsStyles}
            />
            <TableSettingsModal
                droppableId="paginatedTableSettingsModal"
                visible={isSettingsModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                columns={columns}
                showPeriodDeltas={showPeriodDeltas}
                periodDeltaType={periodDeltaType}
                periodDeltaDateRange={periodDeltaDateRange}
                showTotalRow={showTotalRow}
                disablePriorPeriod={disablePriorPeriod ? 'fade' : undefined}
                disabledPriorPeriodMessage={disabledPriorPeriodMessage}
                allowCustomPriorPeriod={allowCustomPriorPeriod}
                dataSourceKey={dataSourceKey}
            />
        </>
    )
}

export default BasicTitle
